"use client";

import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, BookOpen, ListChecks, Play, ShoppingBag } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { StatusBadge } from "@/components/ui/status-badge";
import { useAuth } from "@/components/providers/auth-provider";
import { fetcher } from "@/lib/api";
import { formatLocalPrice } from "@/lib/currency";
import { formatNumber } from "@/lib/format-datetime";
import type { Locale } from "@/lib/i18n";
import type { LearnTrackDetail, LearnViewer } from "@/features/learn/api";
import { trackCheckoutHref, type LearnTrackPrice } from "@/features/learn/lib/track-price";
import { SkillTestCard } from "@/features/learn/skill-test/SkillTestCard";
import { useShownTrackPrice } from "./LearnTrackPriceChip";
import { LearnTrackThumb, trackMark } from "./LearnTrackThumb";

/**
 * The start card of docs/35 §4: the track's artwork, what it costs, the one
 * primary button on the page, and the Skill Test panel beneath it. Redrawn in
 * `LN-UI04`.
 *
 * **What it does not do is the part worth reading.** docs/35 §4 lists "a
 * certificate preview with a mock score". A specimen number on a public page is
 * a claim, so there is none: the Skill Test panel (`SkillTestCard`, `LN-TST01`)
 * shows the scale and "not open yet" until the track's test is published, then
 * the viewer's own state and their own best score. The buy path is `LN-CRT01`.
 *
 * Price comes from the track's own active pricing plans, through the same
 * `planCount` / `fromPrice` pair every course card already uses (docs/07,
 * 2026-09-04). A price of zero is "free to start", not "From ৳0": Learn C has
 * one plan at 0, and the card printed exactly that until `LN-UI04`.
 *
 * `LN-BUY01` (founder 2026-10-08): with exactly one paid plan (`price`), the
 * card shows that plan's price, the struck list price and "one payment, yours
 * for good" in place of "From …", in taka in Bangladesh and in dollars
 * elsewhere, and a secondary "Buy this track" goes straight to checkout with
 * the plan. Before it the only way to buy was the small course link below.
 *
 * A signed-in reader who has started sees their progress and the lesson they
 * are on instead of the price. The lesson is looked up in the syllabus the page
 * already has, so "Continue" opens it in the reader rather than the course
 * player the id alone could point at.
 */
async function fetchTrackViewer(trackSlug: string): Promise<LearnViewer | null> {
  const res = await fetcher<{ track: LearnTrackDetail; viewer: LearnViewer | null }>(
    `/v1/learn/tracks/${encodeURIComponent(trackSlug)}`,
  );
  return res.success ? (res.data.viewer ?? null) : null;
}

export function LearnTrackAside({
  track,
  locale,
  firstLessonHref,
  price = null,
}: {
  track: LearnTrackDetail;
  locale: Locale;
  firstLessonHref: string | null;
  /** `LN-BUY01`: the one paid plan's price, or `null` to keep the "From" line. */
  price?: LearnTrackPrice | null;
}) {
  const t = useTranslations("learn");
  const { isAuthenticated } = useAuth();
  const shownPrice = useShownTrackPrice(price, locale);

  const { data: viewer } = useQuery({
    queryKey: ["learn", "track", track.trackSlug, "viewer"],
    queryFn: () => fetchTrackViewer(track.trackSlug),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  const modules = track.parts.flatMap((part) => part.modules);
  const modulesOut = modules.filter((module) => module.lessons.length > 0).length;
  const upNext = viewer?.continueLessonId
    ? modules
        .flatMap((module) => module.lessons.map((lesson) => ({ lesson, module })))
        .find((entry) => entry.lesson.id === viewer.continueLessonId) ?? null
    : null;

  const continueHref = upNext
    ? `/${locale}${upNext.lesson.href}`
    : viewer?.continueLessonId
      ? `/${locale}/courses/${track.courseSlug}/lessons/${viewer.continueLessonId}`
      : firstLessonHref
        ? `/${locale}${firstLessonHref}`
        : null;
  const started = Boolean(viewer && viewer.progress.completedLessons > 0);
  const n = (value: number) => formatNumber(value, locale);
  const counts = track.lessonAccessCounts;
  const title = (entry: { title_en: string; title_bn: string | null }) =>
    locale === "bn" && entry.title_bn ? entry.title_bn : entry.title_en;

  const includes = [
    { icon: BookOpen, text: t("includeLessons", { lessons: n(track.lessonCount), modules: n(modulesOut) }) },
    { icon: Play, text: t("includeRun") },
    { icon: ListChecks, text: t("includeProblems") },
  ];

  return (
    <aside>
      <Card className="overflow-hidden">
        <LearnTrackThumb
          mark={trackMark(track)}
          thumbnailImageId={track.thumbnailImageId}
          variant="hero"
        />
        <div className="p-5 lg:p-6">
          {viewer?.owned ? (
            <StatusBadge className="mb-3" label={t("ownedLabel")} tone="success" />
          ) : null}

          {started && viewer ? (
            <>
              <div className="flex items-baseline justify-between gap-3">
                <p className="font-[family-name:var(--font-heading)] text-lg font-bold text-[rgb(var(--foreground))]">
                  {t("yourProgress")}
                </p>
                <span className="font-[family-name:var(--font-heading)] text-lg font-bold text-primary-ink">
                  {n(viewer.progress.percent)}%
                </span>
              </div>
              <span aria-hidden className="mt-2.5 block h-2 overflow-hidden rounded-full bg-neutral-100">
                <span
                  className="block h-full rounded-full bg-primary"
                  style={{ width: `${viewer.progress.percent}%` }}
                />
              </span>
              <p className="mt-2 text-[13px] text-neutral-600">
                {t("progressDone", {
                  done: n(viewer.progress.completedLessons),
                  total: n(viewer.progress.totalLessons),
                })}
              </p>
              {upNext ? (
                <div className="mt-4 rounded-[var(--radius)] border border-neutral-100 bg-neutral-50 px-3.5 py-3">
                  <p className="text-xs text-neutral-500">{t("upNext")}</p>
                  <p className="mt-0.5 text-sm font-semibold leading-snug text-[rgb(var(--foreground))]">
                    {title(upNext.lesson)}
                  </p>
                  <p className="mt-0.5 text-xs text-neutral-500">{title(upNext.module)}</p>
                </div>
              ) : null}
            </>
          ) : (
            <>
              {shownPrice && !viewer?.owned ? (
                <>
                  <p className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
                    <span className="font-[family-name:var(--font-heading)] text-3xl font-bold text-[rgb(var(--foreground))]">
                      {shownPrice.amount}
                    </span>
                    {shownPrice.list ? (
                      <>
                        <span className="text-base text-neutral-500 line-through">
                          <span className="sr-only">{t("priceWas")} </span>
                          {shownPrice.list}
                        </span>
                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-800">
                          {t("priceOff", { percent: shownPrice.percentOff })}
                        </span>
                      </>
                    ) : null}
                  </p>
                  <p className="mt-1.5 text-[15px] font-medium text-neutral-800">{t("priceOneTime")}</p>
                </>
              ) : (
                <p className="font-[family-name:var(--font-heading)] text-2xl font-bold text-[rgb(var(--foreground))]">
                  {track.planCount > 0 && track.fromPrice !== null && track.fromPrice > 0
                    ? t("priceFrom", { price: formatLocalPrice(track.fromPrice, "BDT", locale) })
                    : t("priceFree")}
                </p>
              )}
              {counts.free > 0 ? (
                <p className="mt-1.5 text-sm leading-relaxed text-neutral-600">
                  {counts.core > 0
                    ? t("startSubFreeAndCore", { free: n(counts.free), core: n(counts.core) })
                    : t("startSubFree", { free: n(counts.free) })}
                </p>
              ) : null}
            </>
          )}

          {continueHref ? (
            <Button asChild className="mt-5 h-12 w-full text-base font-bold">
              <Link href={continueHref}>
                {started ? t("continueCta") : t("startReading")}
                <ArrowRight aria-hidden className="h-4 w-4" />
              </Link>
            </Button>
          ) : null}

          {price && shownPrice && !viewer?.owned ? (
            <Button asChild className="mt-2.5 h-12 w-full bg-white text-[15px] font-bold" variant="secondary">
              <Link href={trackCheckoutHref(locale, track.courseSlug, price.planId)}>
                <ShoppingBag aria-hidden className="h-4 w-4" />
                {t("buyTrack", { price: shownPrice.amount })}
              </Link>
            </Button>
          ) : null}

          <Link
            className="mt-3 block text-center text-[13px] text-neutral-600 underline decoration-neutral-300 underline-offset-4 transition-colors hover:text-primary-ink hover:decoration-primary"
            href={`/${locale}/courses/${track.courseSlug}`}
          >
            {t("courseLink")}
          </Link>

          <div className="mt-5 border-t border-neutral-100 pt-4">
            <p className="font-[family-name:var(--font-heading)] text-[13px] font-bold text-neutral-600">
              {t("includesHeading")}
            </p>
            <ul className="mt-3 space-y-2.5">
              {includes.map(({ icon: Icon, text }) => (
                <li className="flex items-start gap-2.5 text-sm leading-snug text-neutral-700" key={text}>
                  <Icon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-primary-ink" />
                  {text}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Card>

      {/*
        Its own heading, not "Score bands": the band table lives once on the
        page, in `LearnCertificatesBlock`, and two headings reading the same
        thing is how a page comes to say everything twice.
      */}
      <SkillTestCard locale={locale} open={track.skillTestOpen} trackSlug={track.trackSlug} />
    </aside>
  );
}
